/**
 * Wire protocol shared by the client and the sync server.
 *
 * Everything travelling over the socket is JSON with a `t` discriminator.
 * The server is authoritative: clients send intents, the server validates them
 * and broadcasts the resulting state change to the whole room.
 */

/**
 * `draw` is a freehand stroke rather than a marker, but it travels as a pin on
 * purpose: it is placed, owned, cleared, evicted, persisted and restored by
 * exactly the same rules, and giving it its own message type would mean a
 * second copy of every one of them. What makes it a stroke is `path`.
 */
export const PIN_KINDS = [
  "objective",
  "rally",
  "enemy",
  "loot",
  "extract",
  "danger",
  "self",
  "draw",
] as const;
export type PinKind = (typeof PIN_KINDS)[number];

/** A point on a stroke: `[lat, lng]`, a tuple because a room sends thousands. */
export type StrokePoint = [number, number];

/** How long a transient marker lives, in milliseconds. */
export const PING_TTL_MS = 25_000;

/** Guard rails so one person cannot flood the room. */
export const MAX_PINS_PER_ROOM = 300;

/**
 * A stroke is the one thing here a person can produce by the hundred without
 * meaning to -- a single sweep of the hand is a thousand pointer events -- so
 * it is bounded twice: the client thins the path as it draws, and the server
 * refuses to store more than this many points or this many strokes. Both
 * numbers exist to keep the state file and the broadcast small.
 *
 * The room cap was 40 and that was too low. Over the fortnight after the pen
 * shipped, **one room-day in ten reached 40 lines at once** (217 of 2,123),
 * and going past it takes the oldest line off everyone's map without a word --
 * which is what "ピンが勝手に削除されてしまう" was, reported on 2026-09-02.
 *
 * 150 leaves 99.6% of room-days alone (8 of 2,123 ever reached it). The price
 * is bounded and small: a stroke costs 1.4KB on disk at the median and 4.6KB
 * at the worst seen, so at the busiest moment we have ever had -- 143 rooms
 * drawing at once, on 2026-08-30 -- the ink in the state file goes from about
 * 1.8MB to 2.3MB, and 7.4MB if every line in it were the worst case.
 *
 * `MAX_PINS_PER_ROOM` still bounds the two kinds together, so a room that
 * draws 150 lines has 150 markers left before the oldest starts going.
 */
export const MAX_STROKE_POINTS = 120;
export const MAX_STROKES_PER_ROOM = 150;

/** Socket close codes the client reacts to by name rather than by number. */
export const CLOSE_UNAUTHORIZED = 4001;
export const CLOSE_AT_CAPACITY = 4002;
export const MAX_LABEL_LENGTH = 60;

/**
 * Fill colour for each kind of pin. This is what the eye reads first at a
 * glance, so it carries the meaning ("is that an enemy or an extract?") while
 * the member colour is demoted to the marker's ring.
 *
 * `self` and `draw` have no colour of their own: both are about *who* made
 * them, so the renderer fills them with that member's colour instead.
 */
export const KIND_COLORS: Record<Exclude<PinKind, "self" | "draw">, string> = {
  objective: "#4ea8f2",
  rally: "#43e0c4",
  enemy: "#ff5b5b",
  loot: "#f2c14e",
  extract: "#5ed46a",
  danger: "#ff9b3d",
};

/** Assigned to members in join order so everyone is visually distinct. */
export const MEMBER_COLORS = [
  "#f2c14e",
  "#4ea8f2",
  "#6ddf6d",
  "#f2704e",
  "#c98cf2",
  "#4ef2d2",
  "#f24e9e",
  "#b7c34e",
] as const;

/**
 * Whether a client may draw in this colour.
 *
 * The member palette and nothing else: an open colour field would let a client
 * put anything at all on everyone's screen, and a line the same colour as the
 * map artwork is a line nobody can see. Only strokes get a say -- a pin still
 * wears the colour the room gave you, which is what makes a pin readable as
 * "yours" from across the map.
 */
export function isStrokeColor(value: unknown): value is string {
  return typeof value === "string" && (MEMBER_COLORS as readonly string[]).includes(value);
}

export interface Pin {
  id: string;
  /** Which map the pin belongs to; pins are kept per map so switching back restores them. */
  mapId: string;
  /** Name of the floor/layer it was placed on, or null for the base layer. */
  layer: string | null;
  /** Map coordinates. Leaflet latLng, where lat is the game's z axis and lng its x axis. */
  lat: number;
  lng: number;
  /**
   * Facing, in degrees clockwise from the top of the rendered map.
   * null means the pin has no direction.
   */
  heading: number | null;
  kind: PinKind;
  /**
   * The freehand path, for `draw` pins only; null for every other kind.
   * `lat`/`lng` above repeat the first point, so everything that treats a pin
   * as a single spot -- eviction order, the room snapshot, the map filter --
   * keeps working without knowing what a stroke is.
   */
  path: StrokePoint[] | null;
  label: string;
  authorId: string;
  authorName: string;
  color: string;
  createdAt: number;
  /** Epoch ms after which the pin disappears; null for permanent pins. */
  expiresAt: number | null;
}

/** The fields a client supplies when placing a pin. */
export type PinDraft = Pick<Pin, "mapId" | "layer" | "lat" | "lng" | "heading" | "kind" | "label"> & {
  /** Set for transient markers; the server clamps the lifetime. */
  ttlMs?: number | null;
  /** Set for `draw`; the server thins it and refuses one that is not a line. */
  path?: StrokePoint[] | null;
  /** Set for `draw`; ignored unless it is one of the palette's colours. */
  color?: string;
};

export interface Member {
  id: string;
  name: string;
  avatarUrl: string | null;
  color: string;
  connected: boolean;
  /** Joined through an invite link rather than from the voice call. */
  guest: boolean;
}

export interface RoomState {
  /** The map everyone is looking at. Changing it is a room-wide action. */
  mapId: string;
  pins: Pin[];
  members: Member[];
}

/* ------------------------------------------------------------------ */
/* Client -> server                                                    */
/* ------------------------------------------------------------------ */

export type ClientMessage =
  | { t: "pin.add"; pin: PinDraft }
  | { t: "pin.move"; id: string; lat: number; lng: number; heading?: number | null }
  | { t: "pin.label"; id: string; label: string }
  /** `via` is only for the log: which piece of interface asked. */
  | { t: "pin.remove"; id: string; via?: "eraser" | "card" | "list" | "undo" | "key" }
  | { t: "map.set"; mapId: string }
  | { t: "clear"; scope: "mine" | "all" }
  | { t: "keepalive" };

/* ------------------------------------------------------------------ */
/* Server -> client                                                    */
/* ------------------------------------------------------------------ */

export type ServerMessage =
  | { t: "welcome"; selfId: string; state: RoomState }
  | { t: "pin.add"; pin: Pin }
  | { t: "pin.update"; pin: Pin }
  | { t: "pin.remove"; id: string }
  | { t: "map.set"; mapId: string; byName: string }
  | { t: "members"; members: Member[] }
  | { t: "error"; message: string }
  /**
   * Sent on a timer so the connection is never silent in the downstream
   * direction. Carries nothing: the frame itself is the whole point.
   */
  | { t: "keepalive" };

/** Narrowing helper used on both ends. */
export function isPinKind(value: unknown): value is PinKind {
  return typeof value === "string" && (PIN_KINDS as readonly string[]).includes(value);
}
